import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const GalleryLightbox = ({ images, currentIndex, isOpen, onClose, onNavigate }) => {
  const handleKeyDown = useCallback((e) => {
    if (!isOpen) return;
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowRight') onNavigate(1);
    if (e.key === 'ArrowLeft') onNavigate(-1);
  }, [isOpen, onClose, onNavigate]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const currentItem = images[currentIndex];
  const isVideo = currentItem.type === 'video' || 
                  currentItem.src.toLowerCase().endsWith('.mp4') || 
                  currentItem.src.toLowerCase().endsWith('.mov');

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[200] flex flex-col bg-brand-espresso/98 backdrop-blur-md"
        onClick={onClose}
      >
        {/* Header Controls */}
        <div className="w-full flex justify-between items-center px-6 py-6 md:px-12 md:py-8">
          <div className="text-brand-ivory/30 text-[10px] tracking-[0.4em] uppercase font-sans">
            {currentIndex + 1} &mdash; {images.length}
          </div>
          <button 
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="text-brand-ivory/50 hover:text-brand-ivory transition-colors p-2"
          >
            <SafeIcon name="X" className="w-6 h-6 md:w-8 md:h-8" />
          </button>
        </div>

        {/* Main Viewer - Full Frame Containment */}
        <div className="flex-grow flex items-center justify-center relative overflow-hidden px-4 md:px-24" onClick={(e) => e.stopPropagation()}>
          {/* Desktop Nav Arrows */}
          <button 
            onClick={(e) => { e.stopPropagation(); onNavigate(-1); }}
            className="hidden md:flex absolute left-8 top-1/2 -translate-y-1/2 text-brand-ivory/20 hover:text-brand-ivory transition-all p-6 z-50"
          >
            <SafeIcon name="ChevronLeft" className="w-12 h-12 stroke-[1]" />
          </button>

          <button 
            onClick={(e) => { e.stopPropagation(); onNavigate(1); }}
            className="hidden md:flex absolute right-8 top-1/2 -translate-y-1/2 text-brand-ivory/20 hover:text-brand-ivory transition-all p-6 z-50"
          >
            <SafeIcon name="ChevronRight" className="w-12 h-12 stroke-[1]" />
          </button>

          {/* Media Container */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full h-full flex items-center justify-center"
          >
            {isVideo ? (
              <video 
                src={currentItem.src}
                controls
                autoPlay
                playsInline
                preload="auto"
                className="max-w-full max-h-full object-contain shadow-2xl bg-black/20"
              />
            ) : (
              <img 
                src={currentItem.src} 
                alt={currentItem.alt} 
                className="max-w-full max-h-full object-contain pointer-events-none shadow-2xl"
              />
            )}
          </motion.div>
        </div>

        {/* Mobile Navigation Footer */}
        <div className="w-full py-8 flex justify-center md:hidden">
          <div className="flex items-center gap-20 text-brand-ivory/40">
            <button onClick={(e) => { e.stopPropagation(); onNavigate(-1); }} className="p-4 active:text-brand-ivory">
              <SafeIcon name="ChevronLeft" className="w-8 h-8" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onNavigate(1); }} className="p-4 active:text-brand-ivory">
              <SafeIcon name="ChevronRight" className="w-8 h-8" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GalleryLightbox;